import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Edit, Trash2, Loader2, AlertCircle, Users, Lock, Unlock, Image as ImageIcon } from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

const emptyForm = {
    name: "",
    description: "",
    type: "other",
    privacy: "public",
    entryType: "free",
    price: "0",
    participantLimit: "0",
    status: "active",
    coverImage: "",
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-");

export default function Rooms() {
    const [rooms, setRooms] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [privacyFilter, setPrivacyFilter] = useState("all");

    // Multi-delete selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    const totalPages = useMemo(() => Math.max(Math.ceil(total / limit), 1), [total, limit]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setSearch(searchInput.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchRooms = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const params = {
                page,
                limit,
                search,
            };

            if (privacyFilter !== "all") {
                params.privacy = privacyFilter;
            }

            const { data } = await API.get("/admin/rooms", { params });
            if (!data?.success) {
                throw new Error(data?.message || "Failed to load rooms");
            }

            setRooms(Array.isArray(data.rooms) ? data.rooms : []);
            setTotal(Number(data.total || 0));
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Failed to load rooms");
        } finally {
            setLoading(false);
        }
    }, [page, limit, search, privacyFilter]);

    useEffect(() => {
        fetchRooms();
    }, [fetchRooms]);

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
        setIsFormOpen(false); // Form close karne ke liye
    };

    const handleNewRoomClick = () => {
        setForm(emptyForm);
        setEditingId(null);
        setIsFormOpen((prev) => !prev); // Toggle form (open/close)
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);
            setError("");

            const payload = {
                ...form,
                price: Number(form.price || 0),
                participantLimit: Number(form.participantLimit || 0),
            };

            if (payload.entryType !== "paid") {
                payload.price = 0;
            }

            if (editingId) {
                await API.put(`/admin/rooms/${editingId}`, payload);
            } else {
                await API.post("/admin/rooms", payload);
            }

            resetForm();
            fetchRooms();
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (room) => {
        setEditingId(room._id);
        setForm({
            name: room.name || "",
            description: room.description || "",
            type: room.type || "other",
            privacy: room.privacy || "public",
            entryType: room.entryType || "free",
            price: room.price ?? "0",
            participantLimit: room.participantLimit ?? "0",
            status: room.status || "active",
            coverImage: room.coverImage || "",
        });
        setIsFormOpen(true); // Edit click karne par form automatic open ho jayega
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleToggleSelectAll = () => {
        if (selectedIds.length === rooms.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(rooms.map((r) => r._id));
        }
    };

    const handleToggleSelectRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleBatchDelete = async () => {
        try {
            setIsBatchDeleting(true);
            const { data } = await API.post("/admin/batch-delete", {
                entity: "rooms",
                ids: selectedIds,
            });
            if (data?.success) {
                setSelectedIds([]);
                fetchRooms();
            }
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Batch delete failed");
        } finally {
            setIsBatchDeleting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this room?")) return;

        try {
            await API.delete(`/admin/rooms/${id}`);
            setRooms((prev) => prev.filter((room) => room._id !== id));
            setSelectedIds((prev) => prev.filter((i) => i !== id));
            setTotal((prev) => Math.max(prev - 1, 0));
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Delete failed");
        }
    };

    return (
        <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Manage Rooms</h1>
                <button
                    onClick={handleNewRoomClick}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-pink-500/25"
                >
                    <Plus className="w-5 h-5" />
                    {isFormOpen && !editingId ? "Hide Form" : "New Room"}
                </button>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300 border border-red-200 dark:border-red-500/20">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* Form Section (Conditionally Rendered using isFormOpen) */}
            {isFormOpen && (
                <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-5 space-y-4 shadow-sm transition-all duration-300">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                            {editingId ? "Edit Room Details" : "Create New Room"}
                        </h2>
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            Hide
                        </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            Room Name
                            <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none transition-all"
                                placeholder="Enter room name"
                            />
                        </label>

                        <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            Category Type
                            <select
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-pink-500 outline-none"
                            >
                                <option value="friends">Friends</option>
                                <option value="relationships">Relationships</option>
                                <option value="casual_chat">Casual Chat</option>
                                <option value="late_night_talks">Late Night Talks</option>
                                <option value="fun_games">Fun & Games</option>
                                <option value="other">Other</option>
                            </select>
                        </label>

                        <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            Privacy Level
                            <select
                                value={form.privacy}
                                onChange={(e) => setForm({ ...form, privacy: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-pink-500 outline-none"
                            >
                                <option value="public">Public</option>
                                <option value="private">Private</option>
                            </select>
                        </label>

                        <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            Status
                            <select
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-pink-500 outline-none"
                            >
                                <option value="active">Active</option>
                                <option value="closed">Closed</option>
                            </select>
                        </label>

                        <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            Entry Type
                            <select
                                value={form.entryType}
                                onChange={(e) => setForm({ ...form, entryType: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-pink-500 outline-none"
                            >
                                <option value="free">Free</option>
                                <option value="paid">Paid</option>
                            </select>
                        </label>

                        <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            Entry Price (Coins)
                            <input
                                type="number"
                                min="0"
                                value={form.price}
                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                                disabled={form.entryType !== "paid"}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-slate-800 focus:ring-2 focus:ring-pink-500 outline-none"
                            />
                        </label>

                        <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            Max Participants (0 = ∞)
                            <input
                                type="number"
                                min="0"
                                value={form.participantLimit}
                                onChange={(e) => setForm({ ...form, participantLimit: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-pink-500 outline-none"
                            />
                        </label>

                        <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            Cover Image URL
                            <input
                                value={form.coverImage}
                                onChange={(e) => setForm({ ...form, coverImage: e.target.value })}
                                placeholder="https://example.com/image.jpg"
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-pink-500 outline-none"
                            />
                        </label>
                    </div>

                    <label className="space-y-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 block">
                        Room Description
                        <textarea
                            rows={2}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="What is this room about?"
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-pink-500 outline-none resize-y"
                        />
                    </label>

                    <div className="flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-slate-700">
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-pink-500 text-white rounded-lg font-medium hover:bg-pink-600 disabled:opacity-70 transition-colors shadow-sm"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? <Edit className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
                            {editingId ? "Save Changes" : "Create Room"}
                        </button>
                        <button 
                            type="button" 
                            onClick={resetForm} 
                            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {/* Filters & Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 hide-scrollbar">
                    {[
                        { id: "all", label: "All Rooms" },
                        { id: "public", label: "Public Only" },
                        { id: "private", label: "Private Only" },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => {
                                setPage(1);
                                setPrivacyFilter(tab.id);
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${privacyFilter === tab.id
                                ? "bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400 border border-pink-200 dark:border-pink-500/20"
                                : "bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 border border-transparent"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="relative w-full md:w-72">
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search rooms by name..."
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all"
                    />
                    <svg className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
            </div>

            {/* Admin Data Table View */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left whitespace-nowrap">
                        <thead className="text-xs text-gray-600 dark:text-gray-400 uppercase bg-gray-50/80 dark:bg-slate-900/50 border-b border-gray-200 dark:border-slate-700">
                            <tr>
                                <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                                    <input
                                        type="checkbox"
                                        checked={rooms.length > 0 && selectedIds.length === rooms.length}
                                        onChange={handleToggleSelectAll}
                                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                    />
                                </th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Room Info</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Category</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Access & Entry</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Members</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Status</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Created</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-pink-500" />
                                        <p>Loading rooms data...</p>
                                    </td>
                                </tr>
                            ) : rooms.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-3">
                                                <Users className="w-6 h-6 text-gray-400" />
                                            </div>
                                            <p className="text-base font-medium">No rooms found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                rooms.map((room) => {
                                    const isSelected = selectedIds.includes(room._id);
                                    return (
                                    <tr key={room._id} className={`transition-colors group ${isSelected ? "bg-pink-50/60 dark:bg-pink-500/10" : "hover:bg-gray-50/50 dark:hover:bg-slate-800/50"}`}>
                                        <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleSelectRow(room._id)}
                                                className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-700 flex-shrink-0 border border-gray-200 dark:border-slate-600 flex items-center justify-center">
                                                    {room.coverImage ? (
                                                        <img src={room.coverImage} alt={room.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <ImageIcon className="w-5 h-5 text-gray-400" />
                                                    )}
                                                </div>
                                                <div className="max-w-[200px]">
                                                    <p className="font-semibold text-gray-900 dark:text-white truncate">{room.name}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={room.description}>
                                                        {room.description || "No description provided"}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                            <span className="capitalize text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 px-2.5 py-1 rounded-md text-xs font-medium">
                                                {String(room.type || "other").replaceAll("_", " ")}
                                            </span>
                                        </td>

                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                                                    {room.privacy === "private" ? <Lock className="w-3.5 h-3.5 text-amber-500" /> : <Unlock className="w-3.5 h-3.5 text-green-500" />}
                                                    <span className="capitalize text-xs font-medium">{room.privacy}</span>
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {room.entryType === "paid" ? (
                                                        <span className="text-pink-600 dark:text-pink-400 font-medium">{room.price} Coins</span>
                                                    ) : "Free Entry"}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-4 h-4 text-gray-400" />
                                                <span className="text-gray-700 dark:text-gray-300 font-medium">
                                                    {room.memberCount || 0}
                                                    <span className="text-gray-400 font-normal mx-1">/</span>
                                                    {room.participantLimit ? room.participantLimit : "∞"}
                                                </span>
                                            </div>
                                        </td>

                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${room.status === "active"
                                                ? "bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400"
                                                : "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400"}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${room.status === "active" ? "bg-green-500" : "bg-gray-500"}`}></span>
                                                {room.status === 'active' ? 'Active' : 'Closed'}
                                            </span>
                                        </td>

                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-gray-500 dark:text-gray-400 text-xs">
                                            {formatDate(room.createdAt)}
                                        </td>

                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(room)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10 rounded transition-colors"
                                                    title="Edit Room"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(room._id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 rounded transition-colors"
                                                    title="Delete Room"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Showing page <span className="font-semibold text-gray-900 dark:text-white">{page}</span> of <span className="font-semibold text-gray-900 dark:text-white">{totalPages}</span>
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                            disabled={page === 1 || loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={page >= totalPages || loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Floating Multi-Delete Bar */}
            <BatchActionBar
                selectedCount={selectedIds.length}
                totalCount={total}
                onClearSelection={() => setSelectedIds([])}
                onSelectAll={handleToggleSelectAll}
                onDeleteSelected={handleBatchDelete}
                isDeleting={isBatchDeleting}
                entityName="rooms"
            />
        </div>
    );
}